import * as THREE from 'three';
import Bus from '../entities/Bus.js';
import { InputController } from './InputController.js';
import { SimplePhysics } from '../physics/SimplePhysics.js';
import AudioManager from '../audio/AudioManager.js';
import CollectibleManager from './CollectibleManager.js';
import DebugManager from './DebugManager.js';

// Import manager classes
import SceneManager from '../renderEngine/SceneManager.js';
import SkyboxManager from '../renderEngine/SkyboxManager.js';
import PostProcessingManager from '../renderEngine/PostProcessingManager.js';
import CameraManager from '../renderEngine/CameraManager.js';
import UIManager from '../ui/UIManager.js';
import MinimapManager from '../ui/MinimapManager.js';
import EnvironmentManager from '../environment/EnvironmentManager.js';
import CollisionManager from '../physics/CollisionManager.js';
import TerrainHeightMap from '../terrain/TerrainHeightMap.js';
import PortalManager from '../entities/PortalManager.js';
import WeaponManager from '../entities/WeaponManager.js';
import CubeManager from '../entities/CubeManager.js';
import MeteorManager from '../entities/MeteorManager.js';
import EnemyManager from '../entities/enemies/EnemyManager.js';
import InstanceMeshManager from '../renderEngine/InstanceMeshManager.js';
import ScoreManager from './ScoreManager.js';
import ChallengeManager from './ChallengeManager.js';
// Import game settings
import {
    CAMERA_SETTINGS,
    ENVIRONMENT,
    COIN_SYSTEM,
    COLLISION,
    MINIMAP,
    SMOKE,
    RENDERER,
    CHALLENGES
} from '../constants/gameSettings.js';

export default class Game {
    // Backward-compat getters for code that reads window.game.gasStations / window.game.refuelCost
    get gasStations() { return this.collectibleManager?.getGasStations() ?? []; }
    get refuelCost() { return this.collectibleManager?.getRefuelCost() ?? COIN_SYSTEM.REFUEL_COST; }
    get coins() { return this.collectibleManager?.getCoins() ?? []; }
    get debugMode() { return this.debugManager?.isDebugMode ?? false; }
    get stats() { return this.debugManager?.getStats() ?? { fps: 0, frameTime: 0, frames: 0, lastCheck: 0 }; }

    constructor() {
        // Make game instance accessible globally (for debugging)
        window.game = this;

        // Console game banner
        this.#printGameBanner();
        
        // Initialize core game properties
        this.isRunning = false;
        this.lastTime = 0;
        this.physicsInitialized = false;
        this.clock = new THREE.Clock();
        this.performanceMode = false;
        this.performanceOverlayEnabled = false;
        this.accessibilityPreset = 'default';
        this.nightModeEnabled = false;

        // Initialize renderer
        this.#initRenderer();
        
        // Create scene
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(ENVIRONMENT.BACKGROUND_COLOR);
        this.scene.fog = new THREE.FogExp2(ENVIRONMENT.FOG_COLOR, ENVIRONMENT.FOG_DENSITY);

        // Initialize camera
        this.#initCamera();
        
        // Initialize instance mesh manager
        this.instanceMeshManager = new InstanceMeshManager(this.scene);
        
        // Initialize core systems
        this.physics = new SimplePhysics();
        this.inputController = new InputController(this);
        this.inputController.initMobileController(this);
        this.audioManager = new AudioManager(this.camera, this.scene);
        
        // Bind animate method to ensure proper 'this' context
        this.animate = this.animate.bind(this);
        
        // Set up collision handling
        this.setupCollisionHandling();
        
        // Set up event listeners for window resize and other events
        this.setupEventListeners();
        
        // Initialize debug manager
        this.debugManager = new DebugManager(this.scene, this.physics, this.camera);
        this.debugManager.setup();

        // Initialize collections for game objects
        this.#initGameCollections();
        
        // Initialize Three.js components
        this.initThree();
        
        // Create scene content
        this.createScene();
        
        // Set up post-processing
        this.setupPostProcessing();
        
        // Initialize portal manager
        this.portalManager = new PortalManager(this.scene, this.bus);
    }

    // Private helper methods for constructor
    #printGameBanner() {
        console.log("🚌 Play Tank Up!");
        console.log("This game is 100% made by AI.");
        console.log("Follow me on X https://x.com/junwatu");
        console.log("   ");
    }

    #initRenderer() {
        const isChrome = navigator.userAgent.indexOf("Chrome") !== -1;
        
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            powerPreference: "high-performance",
            precision: isChrome ? "highp" : "mediump",
            alpha: false
        });
        
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.shadowMap.enabled = RENDERER.SHADOWS_ENABLED;
        this.renderer.shadowMap.type = THREE[`${RENDERER.SHADOW_TYPE}ShadowMap`];
        
        document.body.appendChild(this.renderer.domElement);
    }

    #initCamera() {
        this.camera = new THREE.PerspectiveCamera(
            CAMERA_SETTINGS.FOV,
            window.innerWidth / window.innerHeight,
            CAMERA_SETTINGS.NEAR_PLANE,
            CAMERA_SETTINGS.FAR_PLANE
        );
        
        this.camera.position.set(0, 15, 30);
        this.camera.layers.enable(0);  // Enable default layer
        this.camera.layers.enable(2);  // Enable cloud layer
        this.camera.layers.disable(1); // Disable minimap marker layer
        this.camera.lookAt(0, 0, 0);
    }

    #initGameCollections() {
        this.bus = null;
        this.roads = [];
        this.weaponManager = null;
        this.rocks = [];
        this.cacti = [];
        this.trees = [];
        this.obstacles = [];
        this.cubeManager = null;
    }

    // Collision settings are moved to a separate method
    #initCollisionSettings() {
        // Smoke settings
        this.smokeSettings = {
            enabled: SMOKE.ENABLED,
            globalDensity: SMOKE.GLOBAL_DENSITY,
            color: SMOKE.COLOR
        };

        // Collision effects settings
        this.collisionSettings = {
            enabled: COLLISION.ENABLED,
            soundEnabled: COLLISION.SOUND_ENABLED,
            visualEnabled: COLLISION.VISUAL_ENABLED,
            minImpactForSound: COLLISION.MIN_IMPACT_FOR_SOUND,
            minImpactForVisual: COLLISION.MIN_IMPACT_FOR_VISUAL,
            shakeDuration: COLLISION.SHAKE_DURATION,
            shakeIntensity: COLLISION.SHAKE_INTENSITY
        };

        // Collision cooldown to prevent sound spam
        this.lastCollisionTime = 0;
        this.collisionCooldown = COLLISION.COOLDOWN;
    }

    initThree() {
        // Handle window resize
        window.addEventListener('resize', this.onWindowResize.bind(this));

        // Set up lighting
        if (this.physics.debugRenderer) {
            this.scene.add(this.physics.debugRenderer);
        }

        // Call setupMinimap to initialize the minimap
        if (!this.minimapManager) {
            this.setupMinimap();
        }
    }

    setupLights() {
        // Create lights using SceneManager if available
        if (this.sceneManager) {
            const { ambientLight, sunLight } = this.sceneManager.setupLights();
            this.ambientLight = ambientLight;
            this.sunLight = sunLight;
            this.baseAmbientIntensity = ambientLight?.intensity ?? 0.5;
            this.baseSunIntensity = sunLight?.intensity ?? 4.0;
            return;
        }
        
        // Fallback lighting if SceneManager isn't initialized
        const ambientLight = new THREE.AmbientLight(0xE0E0FF, 0.8);
        this.scene.add(ambientLight);
        this.ambientLight = ambientLight;
        this.baseAmbientIntensity = ambientLight.intensity;

        const directionalLight = new THREE.DirectionalLight(0xFFF5E0, 1.6);
        directionalLight.position.set(50, 70, 30);
        directionalLight.castShadow = true;
        this.scene.add(directionalLight);
        this.sunLight = directionalLight;
        this.baseSunIntensity = directionalLight.intensity;
    }

    createScene() {
        console.log("[Game] Creating scene content...");
        
        // Initialize manager classes
        this.sceneManager = new SceneManager(this.scene, this.physics);
        this.skyboxManager = new SkyboxManager(this.scene);
        this.environmentManager = new EnvironmentManager(this.scene, this.physics, this);
        this.uiManager = new UIManager();
        this.uiManager.initialize();

        // Initialize instance mesh manager
        this.instanceMeshManager.initialize();

        // Setup scene lighting
        this.setupLights();

        // Create terrain height map for rolling desert dunes
        this.terrainHeightMap = new TerrainHeightMap({
            terrainSize: ENVIRONMENT.TERRAIN_SIZE,
            seed: 42
        });
        this.scene.userData.terrainHeightMap = this.terrainHeightMap;

        // Create ground and environment
        this.sceneManager.createFloor(ENVIRONMENT.TERRAIN_SIZE, this.terrainHeightMap);
        this.sceneManager.addGroundDecorations(ENVIRONMENT.TERRAIN_SIZE, this.terrainHeightMap);
        this.roads = [];

        // Add environmental elements
        const { trees, rocks, cacti, obstacles } = this.environmentManager.addEnvironmentalElements();
        this.trees = trees;
        this.rocks = rocks;
        this.cacti = cacti;
        this.obstacles = obstacles;

        // Create the player bus
        this.bus = new Bus(this.scene, this.physics, this.inputController);

        // Initialize weapon manager
        this.weaponManager = new WeaponManager(this, this.bus, this.audioManager);

        // Initialize audio for the bus
        this.#initializeAudio();

        // Create skybox and clouds
        this.skybox = this.skyboxManager.createSkybox();
        this.clouds = this.skyboxManager.addClouds();
        this.applyNightMode(this.nightModeEnabled, false);

        // Initialize camera manager
        this.cameraManager = new CameraManager(this.camera, this.renderer, this.scene);
        this.cameraManager.setupCameraControls();
        this.cameraManager.setTarget(this.bus);
        this.configureUISettings();

        // Initialize minimap
        this.minimapManager = new MinimapManager(this.scene, MINIMAP.SIZE);
        const { camera, renderer } = this.minimapManager.setup();
        this.minimapCamera = camera;
        this.minimapRenderer = renderer;
        this.minimapBusMarker = this.minimapManager.createBusMarker(this.bus);
        this.minimapManager.setAccessibilityPreset(this.accessibilityPreset);

        // Initialize collision manager
        this.collisionManager = new CollisionManager(this.scene, this.camera, this.audioManager, this.clock);
        if (this.physicsCollisionListenerAdded && this.fallbackPhysicsCollisionHandler) {
            window.removeEventListener('physics_collision', this.fallbackPhysicsCollisionHandler);
            this.physicsCollisionListenerAdded = false;
        }
        this.collisionManager.setupCollisionHandling();

        // Initialize cube manager
        this.cubeManager = new CubeManager(this.scene, this.physics, this.audioManager);
        this.cubeManager.initialize(100); // Fewer cubes for better performance

        // Initialize enemy manager
        this.enemyManager = new EnemyManager(this.scene, this.physics, this.audioManager, this.bus);
        this.enemyManager.initialize();

        // Initialize meteor manager
        this.meteorManager = new MeteorManager(this.scene, this.physics, this.audioManager);
        // Force disable the meteor manager until game.start() is called
        if (this.meteorManager) {
            console.log("[Game] Ensuring meteor manager is disabled during initialization");
            this.meteorManager.setEnabled(false);
            // Force reset any potential in-progress events
            if (this.meteorManager.config) {
                this.meteorManager.config.eventInProgress = false;
                this.meteorManager.config.meteorsSpawned = false;
                this.meteorManager.config.nextEventTime = Number.MAX_SAFE_INTEGER;
            }
        }
// Do not start meteor timer yet; it will be started in Game.start()
        
        // Initialize portal manager
        this.portalManager = new PortalManager(this.scene, this.bus);
        
        // Once the bus is created, set it in the meteorManager and pass the game reference
        if (this.bus && this.meteorManager) {
            this.meteorManager.setBus(this.bus);
            this.meteorManager.setGame(this);
            this.meteorManager.setAccessibilityPreset(this.accessibilityPreset);
        }

        // Initialize score manager
        this.scoreManager = new ScoreManager();
        this.scoreManager.onScoreChange = (score, points, type) => {
            this.uiManager?.updateScoreDisplay?.(score, points, type);
        };
        this.scoreManager.onMultiplierChange = (multiplier) => {
            this.uiManager?.updateMultiplierDisplay?.(multiplier);
        };
        this.scoreManager.onComboChange = (combo) => {
            this.uiManager?.updateComboDisplay?.(combo);
        };

        // Initialize challenge manager (disabled until game.start())
        this.challengeManager = new ChallengeManager(this.scene, this.bus, this.scoreManager);
        this.challengeManager.cooldownTimer = CHALLENGES.ENABLED ? CHALLENGES.INITIAL_DELAY : Number.MAX_SAFE_INTEGER;
        this.challengeManager.timeBetweenChallenges = CHALLENGES.TIME_BETWEEN;
        this.challengeManager.onChallengeStart = (ch) => {
            this.uiManager?.showChallengeNotification?.(ch, 'start');
        };
        this.challengeManager.onChallengeComplete = (ch) => {
            this.uiManager?.showChallengeNotification?.(ch, 'complete');
        };
        this.challengeManager.onChallengeFail = (ch, reason) => {
            this.uiManager?.showChallengeNotification?.(ch, 'fail', reason);
        };
        this.challengeManager.onChallengeProgress = (status) => {
            this.uiManager?.updateChallengeHUD?.(status);
        };

        // Wire up meteor manager so survival challenges can trigger meteor waves
        if (this.meteorManager) {
            this.challengeManager.setMeteorManager(this.meteorManager);
        }

        // Initialize collectible manager (coins, gas stations)
        this.collectibleManager = new CollectibleManager(this.scene, this.physics, this.terrainHeightMap);
        this.collectibleManager.setBus(this.bus);
        this.collectibleManager.setAudioManager(this.audioManager);
        this.collectibleManager.setScoreManager(this.scoreManager);
        this.collectibleManager.setUIManager(this.uiManager);
        this.collectibleManager.initialize();
    }
    
    #initializeAudio() {
        if (!this.audioManager || !this.bus || !this.bus.mesh) return;
        
        // Start engine idle sound
        this.audioManager.playEngineIdle();

        // Attach sounds to the bus for proper positional audio
        this.audioManager.attachSound('engine_idle', this.bus.mesh);
        this.audioManager.attachSound('engine_accelerate', this.bus.mesh);
        this.audioManager.attachSound('braking', this.bus.mesh);
        this.audioManager.attachSound('horn', this.bus.mesh);
    }


    start() {
        console.log("[Game] Starting game...");

        // Stop preparation render loop if it's running
        this.preparationLoopActive = false;

        // Reset input controller state — ensures no stale isGameOver or stuck keys
        if (this.inputController) {
            this.inputController.isGameOver = false;
            this.inputController.keys = {};
            console.log("[Game] InputController reset: isGameOver=false, keys cleared");
        }

        // Reset bus fuel timer so welcome-screen idle time doesn't drain fuel on first frame
        if (this.bus) {
            this.bus.fuelLastUpdate = performance.now();
            // Also ensure engine is operational at game start
            if (this.bus.fuel > 0) {
                this.bus.enginePower = 300;
                this.bus.reverseEnginePower = 80;
            }
            console.log("[Game] Bus fuel timer reset, enginePower:", this.bus.enginePower);
        }
        
        // Setup fuel update listener (if not already set up in prepare())
        if (!this.fuelListenerAdded) {
            this._boundUpdateFuelDisplay = this.updateFuelDisplay.bind(this);
            window.addEventListener('fuel_updated', this._boundUpdateFuelDisplay);
            this.fuelListenerAdded = true;
        }

        // Add coins if not already added in prepare()
        if (!this.coinsAdded && this.collectibleManager) {
            setTimeout(() => {
                if (this.physics.isInitialized) {
                    this.collectibleManager.initializeCoins();
                } else {
                    console.error("Physics not initialized after delay");
                }
            }, 500);
            this.coinsAdded = true;
        }

        // Initialize audio system if not already done
        if (!this.audioInitialized) {
            this.#initializeAudioSystem();
            this.audioInitialized = true;
        }

        // Hide loading screen if not already done
        this.#hideLoadingScreen();

        // Reset meteor event timer so meteors only start after game starts
        if (this.meteorManager && typeof this.meteorManager.resetNextEventTime === 'function') {
            console.log("[Game] Resetting and enabling meteor manager");
            this.meteorManager.resetNextEventTime();
            this.meteorManager.setEnabled(true); // Enable meteor events after game starts
        } else {
            console.warn("[Game] Meteor manager not available");
        }

        // Start the game
        this.isRunning = true;

        // Reset the clock so the first getDelta() returns ~0 instead of
        // the entire welcome-screen wait time (which would catapult the bus
        // underground with seconds of accumulated gravity).
        this.clock = new THREE.Clock();

        console.log("[Game] Game is now running");

        // Start animation loop with slight delay to ensure components are ready
        setTimeout(() => {
            this.animate();
            console.log('[Game] Game animation loop started');
        }, 50);
    }
    
    #initializeUI() {
        // Create a flag to track if speed display has been initialized
        this.speedDisplayInitialized = false;
        
        // Use UIManager if available
        if (this.uiManager) {
            this.uiManager.createFuelDisplay();
            this.uiManager.createCoinsDisplay();
            this.speedDisplayInitialized = true;
            return;
        }
        // Legacy speed display creation removed. All speed UI is now managed by HUDManager/UIManager.
    }
    
    #initializeAudioSystem() {
        if (!this.audioManager) return;
        
        this.audioManager.init()
            .then(() => {
                console.log('Audio initialized successfully');
                
                // Check if horn sound is loaded
                if (this.audioManager.sounds['horn'] && 
                    this.audioManager.sounds['horn'].sound.buffer) {
                    console.log('Horn sound loaded successfully');
                } else {
                    console.warn('Horn sound not loaded correctly');
                }
            })
            .catch(error => {
                console.error('Error initializing audio:', error);
            });
    }
    
    #hideLoadingScreen() {
        const loadingScreen = document.getElementById('loading-screen');
        if (loadingScreen) {
            loadingScreen.style.display = 'none';
        }
    }
    
    updateFuelDisplay(event) {
        // Use UIManager if available
        if (this.uiManager) {
            this.uiManager.updateFuelDisplay(event);
            return;
        }

        // Validate event data
        if (!event || !event.detail) {
            console.warn("Invalid fuel event data in updateFuelDisplay");
            return;
        }

        // Create fuel display elements if they don't exist
        if (!this.fuelBar || !this.fuelText) {
            this.#createFuelDisplayElements();
        }

        // Update display with event data
        const { level, capacity, percentage, empty } = event.detail;
        this.#updateFuelDisplayValues(percentage, empty);
    }
    
    #createFuelDisplayElements() {
        // Create fuel container
        this.fuelContainer = document.createElement('div');
        this.fuelContainer.id = 'fuel-container';
        this.fuelContainer.style.position = 'fixed';
        this.fuelContainer.style.top = '20px';
        this.fuelContainer.style.right = '20px';
        this.fuelContainer.style.width = '60px';
        this.fuelContainer.style.textAlign = 'center';
        this.fuelContainer.style.color = 'white';
        this.fuelContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
        this.fuelContainer.style.padding = '10px';
        this.fuelContainer.style.borderRadius = '5px';

        // Create fuel label
        const fuelLabel = document.createElement('div');
        fuelLabel.textContent = 'FUEL';
        fuelLabel.style.fontSize = '12px';
        fuelLabel.style.fontWeight = 'bold';
        fuelLabel.style.marginBottom = '5px';

        // Create fuel gauge background
        const fuelBarContainer = document.createElement('div');
        fuelBarContainer.style.width = '30px';
        fuelBarContainer.style.height = '150px';
        fuelBarContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
        fuelBarContainer.style.borderRadius = '10px';
        fuelBarContainer.style.overflow = 'hidden';
        fuelBarContainer.style.border = '1px solid rgba(255, 255, 255, 0.3)';
        fuelBarContainer.style.margin = '0 auto';
        fuelBarContainer.style.position = 'relative';

        // Create fuel gauge fill
        this.fuelBar = document.createElement('div');
        this.fuelBar.style.width = '100%';
        this.fuelBar.style.height = '100%';
        this.fuelBar.style.backgroundColor = '#2CFF2C';
        this.fuelBar.style.borderRadius = '8px';
        this.fuelBar.style.transition = 'height 0.3s, background-color 0.3s';
        this.fuelBar.style.position = 'absolute';
        this.fuelBar.style.bottom = '0';
        this.fuelBar.style.left = '0';

        // Create fuel percentage text
        this.fuelText = document.createElement('div');
        this.fuelText.textContent = '100%';
        this.fuelText.style.marginTop = '5px';
        this.fuelText.style.fontSize = '14px';

        // Assemble fuel gauge components
        fuelBarContainer.appendChild(this.fuelBar);
        this.fuelContainer.appendChild(fuelLabel);
        this.fuelContainer.appendChild(fuelBarContainer);
        this.fuelContainer.appendChild(this.fuelText);

        document.body.appendChild(this.fuelContainer);
    }
    
    #updateFuelDisplayValues(percentage, empty) {
        // Update fuel percentage text
        this.fuelText.textContent = `${Math.round(percentage)}%`;

        // Update fuel bar height for vertical gauge
        this.fuelBar.style.height = `${percentage}%`;

        // Change fuel bar color based on fuel level
        if (percentage <= 10) {
            // Critical - red and flashing
            this.fuelBar.style.backgroundColor = '#FF3300';

            // Add flashing effect when critically low
            if (!this.fuelFlashInterval && percentage <= 5) {
                this.fuelFlashInterval = setInterval(() => {
                    this.fuelBar.style.opacity = this.fuelBar.style.opacity === '1' ? '0.3' : '1';
                }, 500);
            }
        } else if (percentage <= 30) {
            // Low - orange
            this.fuelBar.style.backgroundColor = '#FFAA00';

            // Clear flash interval if it exists
            this.#clearFuelFlashInterval();
        } else {
            // Good - green
            this.fuelBar.style.backgroundColor = '#2CFF2C';

            // Clear flash interval if it exists
            this.#clearFuelFlashInterval();
        }

        // Show warning if out of fuel
        if (empty && !this.outOfFuelWarning) {
            this.showOutOfFuelWarning();
        }

        // Dismiss game-over overlay if fuel was restored
        if (!empty && this.outOfFuelWarning) {
            if (this.outOfFuelWarning.parentNode) {
                this.outOfFuelWarning.parentNode.removeChild(this.outOfFuelWarning);
            }
            this.outOfFuelWarning = null;
        }
    }

    #clearFuelFlashInterval() {
        if (this.fuelFlashInterval) {
            clearInterval(this.fuelFlashInterval);
            this.fuelFlashInterval = null;
            this.fuelBar.style.opacity = '1';
        }
    }

    showOutOfFuelWarning() {
        // Delegate to UIManager (legacy fallback path)
        if (this.uiManager) {
            this.uiManager.showOutOfFuelWarning();
        }
    }

    showNotificationMessage(message, backgroundColor = "rgba(0, 100, 180, 0.8)") {
        // Use UIManager to show notification message
        if (this.uiManager) {
            this.uiManager.showNotificationMessage(message, backgroundColor);
        }
    }

    showRefuelingNotification(stationName = '', refuelCost = 10) {
        // Use UIManager to show refueling notification
        if (this.uiManager) {
            this.uiManager.showRefuelingNotification(stationName, refuelCost);
        }
    }

    // Removed updateSpeedDisplay in favor of unified updateSpeed routing through UIManager/HUDManager
    // All speed updates should use this.uiManager.updateSpeed(busSpeed) where busSpeed is in m/s
    // The UIManager/HUDManager will handle conversion and updating the UI.
    

    updateCoinsDisplay(coins) {
        // Determine current coin count
        const coinCount = this.#determineCoinCount(coins);

        // Use UIManager if available
        if (this.uiManager) {
            this.uiManager.updateCoinsDisplay(coinCount);
            return;
        }

        // Create or update the coins display element
        this.#ensureCoinsDisplayExists();
        
        // Update the display content
        this.coinsDisplay.textContent = `Coins: ${coinCount}`;
    }
    
    #determineCoinCount(coins) {
        // If coins parameter is provided, use it
        if (coins !== undefined) {
            return coins;
        }
        
        // If bus exists, get coins from bus
        if (this.bus) {
            return this.bus.coins || 0;
        }
        
        // Default fallback
        return 0;
    }
    
    #ensureCoinsDisplayExists() {
        if (this.coinsDisplay) return;
        
        this.coinsDisplay = document.createElement('div');
        this.coinsDisplay.id = 'coins-display';
        this.coinsDisplay.style.position = 'fixed';
        this.coinsDisplay.style.top = '80px';
        this.coinsDisplay.style.left = '20px';
        this.coinsDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
        this.coinsDisplay.style.color = '#FFD700';
        this.coinsDisplay.style.padding = '10px 15px';
        this.coinsDisplay.style.borderRadius = '5px';
        this.coinsDisplay.style.fontFamily = 'Arial, sans-serif';
        this.coinsDisplay.style.fontSize = '20px';
        this.coinsDisplay.style.zIndex = '100';
        document.body.appendChild(this.coinsDisplay);
    }

    onWindowResize() {
        // Update camera aspect ratio
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();

        // Update renderer size
        this.renderer.setSize(window.innerWidth, window.innerHeight);

        // Update post-processing using PostProcessingManager
        if (this.postProcessingManager) {
            this.postProcessingManager.onWindowResize();
        }

        // Update camera manager
        if (this.cameraManager) {
            this.cameraManager.onWindowResize(window.innerWidth, window.innerHeight);
        }
    }


    updateCamera() {
        // Use CameraManager to update camera
        if (this.cameraManager) {
            const deltaTime = this.clock.getDelta();
            const gamepadState = this.inputController?.getGamepadState?.();
            this.cameraManager.setFollowLookInput(
                gamepadState?.rightStickX || 0,
                gamepadState?.rightStickY || 0
            );
            this.cameraManager.updateCamera(deltaTime);
        }
    }


    setupPostProcessing() {
        if (!this.postProcessingManager) {
            this.postProcessingManager = new PostProcessingManager(this.renderer, this.scene, this.camera);
        }
        this.composer = this.postProcessingManager.setup();
        this.fxaaPass = this.postProcessingManager.fxaaPass; // If needed elsewhere
        console.log("[Game] Post-processing setup complete.");
    }

    setupMinimap() {
        // Ensure manager exists or create it
        if (!this.minimapManager) {
            this.minimapManager = new MinimapManager(this.scene, MINIMAP.SIZE);
        }
        const { camera, renderer } = this.minimapManager.setup();
        this.minimapCamera = camera;
        this.minimapRenderer = renderer;

        // Create bus marker if bus exists (might be called before createScene)
        if (this.bus) {
            this.minimapBusMarker = this.minimapManager.createBusMarker(this.bus);
        }
        console.log("[Game] Minimap setup initiated.");
    }

    createRoadMap() {
        // Create a group for road map elements
        this.roadMapGroup = new THREE.Group();

        // Skip road visualization since roads have been removed

        // Grid helper has been removed
        // No grid in minimap as requested

        // Add the road map group to the scene
        this.scene.add(this.roadMapGroup);
    }

    addCompassRose(container) {
        // Create compass rose container
        const compassContainer = document.createElement('div');
        compassContainer.style.position = 'absolute';
        compassContainer.style.top = '10px';
        compassContainer.style.right = '10px';
        compassContainer.style.width = '40px';
        compassContainer.style.height = '40px';
        compassContainer.style.color = 'white';
        compassContainer.style.fontSize = '12px';
        compassContainer.style.fontFamily = 'Arial, sans-serif';
        compassContainer.style.userSelect = 'none';

        // Add cardinal directions
        const directions = [
            { text: 'N', top: '0px', left: '50%' },
            { text: 'E', top: '50%', left: '100%' },
            { text: 'S', top: '100%', left: '50%' },
            { text: 'W', top: '50%', left: '0px' }
        ];

        directions.forEach(dir => {
            const dirElement = document.createElement('div');
            dirElement.textContent = dir.text;
            dirElement.style.position = 'absolute';
            dirElement.style.transform = 'translate(-50%, -50%)';
            dirElement.style.top = dir.top;
            dirElement.style.left = dir.left;
            compassContainer.appendChild(dirElement);
        });

        container.appendChild(compassContainer);
    }

    createMinimapBusMarker() {
        // Create a triangle to represent the bus on the minimap
        // Vertices now create a triangle pointing along the forward Z axis (0,0,1)
        const markerGeometry = new THREE.BufferGeometry();
        const vertices = new Float32Array([
            0, 0, 1.5,    // tip (front)
            -1, 0, -1,    // left (back)
            1, 0, -1      // right (back)
        ]);
        markerGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

        // Create a more distinctive material for the marker
        const markerMaterial = new THREE.MeshBasicMaterial({
            color: 0xff0000,  // Bright red
            side: THREE.DoubleSide
        });

        this.minimapBusMarker = new THREE.Mesh(markerGeometry, markerMaterial);
        this.minimapBusMarker.scale.set(5, 5, 5); // Make it larger and more visible

        // Rotate the marker to be flat on the XZ plane
        this.minimapBusMarker.rotation.x = -Math.PI / 2;

        // Raise the marker slightly above the ground to prevent z-fighting
        this.minimapBusMarker.position.y = 1;

        // Make sure the marker is only visible on the minimap by setting it to layer 1
        this.minimapBusMarker.layers.set(1);

        // Add a second shape for better visibility - a circle below the triangle
        const circleGeometry = new THREE.CircleGeometry(1.5, 16);
        const circleMaterial = new THREE.MeshBasicMaterial({
            color: 0xffff00, // Yellow for contrast
            side: THREE.DoubleSide
        });

        const circleMarker = new THREE.Mesh(circleGeometry, circleMaterial);
        circleMarker.rotation.x = -Math.PI / 2; // Rotate to be flat
        circleMarker.position.y = 0.1; // Position slightly above ground
        circleMarker.position.z = -0.5; // Position it slightly behind the triangle
        circleMarker.layers.set(1); // Same layer as triangle - only visible in minimap

        // Create a group to hold both shapes
        this.minimapBusMarkerGroup = new THREE.Group();
        this.minimapBusMarkerGroup.add(this.minimapBusMarker);
        this.minimapBusMarkerGroup.add(circleMarker);

        this.scene.add(this.minimapBusMarkerGroup);
    }

    updateMinimap() {
        if (!this.minimapRenderer || !this.minimapCamera) return;

        // Update bus marker position and rotation
        if (this.minimapBusMarkerGroup && this.bus && this.bus.mesh) {
            // Copy position from bus
            this.minimapBusMarkerGroup.position.copy(this.bus.mesh.position);

            // Get the bus's forward direction and apply it to the marker
            this.minimapBusMarkerGroup.rotation.y = this.bus.mesh.rotation.y;

            // Ensure marker is visible
            this.minimapBusMarkerGroup.visible = true;
        } else if (this.minimapBusMarkerGroup) {
            // Hide marker if bus doesn't exist yet
            this.minimapBusMarkerGroup.visible = false;
        }

        // Follow bus with minimap camera
        if (this.bus && this.bus.mesh) {
            const busPosition = this.bus.mesh.position.clone();
            this.minimapCamera.position.set(busPosition.x, 200, busPosition.z);
            this.minimapCamera.lookAt(busPosition.x, 0, busPosition.z);
        } else {
            // Default camera position if bus doesn't exist yet
            this.minimapCamera.position.set(0, 200, 0);
            this.minimapCamera.lookAt(0, 0, 0);
        }

        // Render minimap
        this.minimapRenderer.render(this.scene, this.minimapCamera);
    }

    animate() {
        if (!this.isRunning) return;

        // Request next frame early for better performance
        requestAnimationFrame(this.animate);

        // Calculate delta time and update stats
        const deltaTime = this.clock.getDelta();
        // Cap delta time to prevent physics explosions after tab-away or long frames
        const clampedDelta = Math.min(deltaTime, 0.05); // max ~20 FPS equivalent
        this.debugManager.updateStats();

        try {
            // Update core systems
            this.#updateCoreGameSystems(clampedDelta);
        } catch (e) {
            console.error('[Game] Error in core systems update:', e);
        }

        try {
            // Update game entities
            this.#updateGameEntities(clampedDelta);
        } catch (e) {
            console.error('[Game] Error in game entities update:', e);
        }

        try {
            // Update camera and visual effects
            this.#updateCameraAndEffects(clampedDelta);
        } catch (e) {
            console.error('[Game] Error in camera/effects update:', e);
        }

        // --- Ensure speed display updates every frame ---
        if (this.bus && this.uiManager) {
            this.uiManager.updateSpeed(this.bus.getSpeed()); // pass m/s
        }

        // Update UI and debug info
        this.#updateUIAndDebugInfo();

        // Render the scene
        this.renderer.render(this.scene, this.camera);
    }
    
    #updateCoreGameSystems(deltaTime) {
        // Poll keyboard/gamepad/mobile inputs each frame.
        if (this.inputController && typeof this.inputController.pollInputSources === 'function') {
            this.inputController.pollInputSources();
        }

        // Update physics
        if (this.physics) {
            this.physics.update(deltaTime);
        }
    }
    
    #updateGameEntities(deltaTime) {
        // Update bus
        if (this.bus) {
            this.bus.update(deltaTime, this.inputController);
        }

  
        // Update weapons
        if (this.weaponManager) {
            this.weaponManager.update(deltaTime);
        }        
        // Update collectibles (gas stations, coins)
        if (this.collectibleManager) {
            this.collectibleManager.update(deltaTime);
        }
        
        // Update floating cube
        if (this.floatingCube) {
            this.floatingCube.update();
        }
        
        // Update cube manager
        if (this.cubeManager) {
            this.cubeManager.update(this.bus);
        }

        // Update enemies
        if (this.enemyManager && this.isRunning) {
            this.enemyManager.update(deltaTime);
        }

        // Update meteor manager only when game is running
        if (this.meteorManager && this.isRunning) {
            this.meteorManager.update(deltaTime);
        }

        // Update score manager
        if (this.scoreManager && this.bus) {
            this.scoreManager.update(deltaTime, this.bus);
        }

        // Update challenge manager
        if (this.challengeManager && this.isRunning) {
            this.challengeManager.update(deltaTime);
        }
    }
    
    #updateCameraAndEffects(deltaTime) {
        const gamepadState = this.inputController?.getGamepadState?.();

        // Update camera
        if (this.cameraManager) {
            this.cameraManager.setFollowLookInput(
                gamepadState?.rightStickX || 0,
                gamepadState?.rightStickY || 0
            );
            this.cameraManager.updateCamera(deltaTime);
        }
        
        // Update collision camera shake
        if (this.collisionManager) {
            this.collisionManager.updateCameraShake();
        }
        
        // Update post-processing effects
        if (this.postProcessingManager) {
            this.postProcessingManager.update(deltaTime);
        }
        
        // Update minimap
        if (this.minimapManager && this.bus) {
            this.minimapManager.updateMinimap(this.bus);
        }
    }
    
    #updateUIAndDebugInfo() {
        this.uiManager?.updatePerformanceOverlay?.(this.stats.fps, this.stats.frameTime);

        // Update debug info if debug mode is enabled
        if (this.debugMode) {
            this.debugManager.updateDebugInfo(this.bus, this.gasStations);
        }
    }

    stop() {
        console.log("[Game] Stopping game...");
        
        // Stop animation loop
        this.isRunning = false;
        
        // Clean up resources in order
        this.#cleanupResourcesAndManagers();
        this.#cleanupGameObjects();
        this.#cleanupIntervals();
        this.#removeEventListeners();
        
        console.log("[Game] Game stopped and resources cleaned up.");
    }
    
    #cleanupResourcesAndManagers() {
        // Clean up controllers and managers
        if (this.inputController && typeof this.inputController.cleanup === 'function') this.inputController.cleanup();
        if (this.physics && typeof this.physics.cleanup === 'function') this.physics.cleanup();
        if (this.audioManager && typeof this.audioManager.dispose === 'function') this.audioManager.dispose();
        if (this.weaponManager && typeof this.weaponManager.dispose === 'function') this.weaponManager.dispose();
        if (this.minimapManager && typeof this.minimapManager.dispose === 'function') this.minimapManager.dispose();
        if (this.collisionManager && typeof this.collisionManager.dispose === 'function') this.collisionManager.dispose();
        
        // Additional managers that might need cleanup
        if (this.postProcessingManager && typeof this.postProcessingManager.dispose === 'function') this.postProcessingManager.dispose();
        if (this.environmentManager && typeof this.environmentManager.dispose === 'function') this.environmentManager.dispose();
        if (this.cubeManager && typeof this.cubeManager.clear === 'function') this.cubeManager.clear();
        if (this.enemyManager && typeof this.enemyManager.clear === 'function') this.enemyManager.clear();
        if (this.meteorManager && typeof this.meteorManager.dispose === 'function') this.meteorManager.dispose();
        if (this.portalManager && typeof this.portalManager.dispose === 'function') this.portalManager.dispose();
        if (this.challengeManager && typeof this.challengeManager.dispose === 'function') this.challengeManager.dispose();
    }
    
    #cleanupGameObjects() {
        // Clean up collectibles (coins, gas stations)
        if (this.collectibleManager) {
            this.collectibleManager.clear();
        }

        if (this.trees && this.trees.length) {
            this.trees.forEach(tree => tree?.remove());
            this.trees = [];
        }
        
        if (this.rocks && this.rocks.length) {
            this.rocks.forEach(rock => rock?.remove());
            this.rocks = [];
        }
        
        if (this.cacti && this.cacti.length) {
            this.cacti.forEach(cactus => cactus?.remove());
            this.cacti = [];
        }
    }
    
    #cleanupIntervals() {
        // Clear any active intervals
        if (this.fuelFlashInterval) {
            clearInterval(this.fuelFlashInterval);
            this.fuelFlashInterval = null;
        }
    }
    
    #removeEventListeners() {
        // Remove window event listeners
        window.removeEventListener('resize', this.onWindowResize);
        if (this._boundUpdateFuelDisplay) {
            window.removeEventListener('fuel_updated', this._boundUpdateFuelDisplay);
        }
        window.removeEventListener('keydown', this.handleKeyDown);
        if (this.physicsCollisionListenerAdded && this.fallbackPhysicsCollisionHandler) {
            window.removeEventListener('physics_collision', this.fallbackPhysicsCollisionHandler);
            this.physicsCollisionListenerAdded = false;
        }
        
        // Remove renderer event listeners if they exist
        if (this.renderer && this.renderer.domElement) {
            this.renderer.domElement.removeEventListener('dblclick', 
                this.cameraManager?.resetCameraView);
        }
    }


    updateUI() {
        // Fuel display
        if (this.bus && this.bus.fuel && this.uiManager) {
            const fuelBar = this.uiManager.createFuelDisplay();
            if (fuelBar) {
                // Create fuel event handler
                document.addEventListener('fuel-update', (event) => this.uiManager.updateFuelDisplay(event));
                // Remove the duplicate event listener that causes double speed updates
            }
        }

            
        // Don't call updateUI() on every frame as it leads to duplicate displays
        // Only update the speed display directly
        this.updateSpeedDisplay();
    }



    // Add a new method to handle physics collisions
    setupCollisionHandling() {
        // Initialize collision settings
        this.#initCollisionSettings();
        
        // Use CollisionManager if available
        if (this.collisionManager) {
            if (this.physicsCollisionListenerAdded && this.fallbackPhysicsCollisionHandler) {
                window.removeEventListener('physics_collision', this.fallbackPhysicsCollisionHandler);
                this.physicsCollisionListenerAdded = false;
            }
            return;
        }

        if (this.physicsCollisionListenerAdded) return;

        this.fallbackPhysicsCollisionHandler = (event) => {
            this.handleCollision(event.detail);
        };
        window.addEventListener('physics_collision', this.fallbackPhysicsCollisionHandler);
        this.physicsCollisionListenerAdded = true;
    }

    // Handle collision event
    handleCollision(collisionData) {
        const { bodyA, bodyB, position, normal, impactForce, impactSpeed } = collisionData;

        // Ensure collisionSettings exists to prevent TypeError
        if (!this.collisionSettings) {
            // Initialize collision settings if they don't exist
            this.#initCollisionSettings();
        }

        // Skip if collision effects are disabled
        if (!this.collisionSettings.enabled) return;

        // Skip if in collision cooldown period to prevent sound spam
        if (this.#shouldSkipCollisionDueToCooldown()) return;
        
        // Record time of this collision
        this.lastCollisionTime = performance.now();

        // Generate sound effects if appropriate
        this.#handleCollisionSound(position, impactForce);

        // Generate visual effects if appropriate
        this.#handleCollisionVisuals(position, normal, impactForce);

        // Log collision in debug mode
        if (this.debugMode) {
            console.log(`Collision: Impact force = ${impactForce.toFixed(2)}, Speed = ${impactSpeed.toFixed(2)}`);
        }
    }
    
    #shouldSkipCollisionDueToCooldown() {
        const now = performance.now();
        return (now - this.lastCollisionTime < this.collisionCooldown);
    }
    
    #handleCollisionSound(position, impactForce) {
        if (!this.collisionSettings.soundEnabled || 
            impactForce <= this.collisionSettings.minImpactForSound || 
            !this.audioManager) return;
            
        // Additional check for audio manager's collision sounds setting
        if (this.audioManager.collisionSoundsEnabled === false) return;
        
        // Play collision sound at collision position
        this.audioManager.playCollisionSound(position, impactForce);
    }
    
    #handleCollisionVisuals(position, normal, impactForce) {
        if (!this.collisionSettings.visualEnabled || 
            impactForce <= this.collisionSettings.minImpactForVisual) return;
        
        // Create collision particles
        this.createCollisionParticles(position, normal, impactForce);

        // Apply camera shake effect based on impact force
        if (this.followCameraEnabled) {
            const shakeAmount = Math.min(
                this.collisionSettings.shakeIntensity,
                impactForce / 300 * this.collisionSettings.shakeIntensity
            );
            this.applyCameraShake(shakeAmount, this.collisionSettings.shakeDuration);
        }
    }

    // Create particle effect at collision point
    createCollisionParticles(position, normal, impactForce) {
        // Create particle group
        const particleCount = Math.min(50, Math.floor(impactForce / 10));
        const particleGeometry = new THREE.BufferGeometry();
        const particlePositions = new Float32Array(particleCount * 3);
        const particleSizes = new Float32Array(particleCount);

        // Generate particles along the collision normal
        for (let i = 0; i < particleCount; i++) {
            // Position with some randomness
            const offset = new THREE.Vector3(
                (Math.random() - 0.5) * 1.0,
                (Math.random() - 0.5) * 1.0,
                (Math.random() - 0.5) * 1.0
            );

            // Add bias in the direction of the collision normal
            const normalBias = new THREE.Vector3().copy(normal).multiplyScalar(Math.random() * 0.5);
            offset.add(normalBias);

            // Set particle position
            const particlePos = new THREE.Vector3().copy(position).add(offset);
            particlePositions[i * 3] = particlePos.x;
            particlePositions[i * 3 + 1] = particlePos.y;
            particlePositions[i * 3 + 2] = particlePos.z;

            // Set particle size based on impact force
            particleSizes[i] = 0.1 + Math.random() * 0.3 * (impactForce / 100);
        }

        // Set geometry attributes
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
        particleGeometry.setAttribute('size', new THREE.BufferAttribute(particleSizes, 1));

        // Create particle material
        const particleMaterial = new THREE.PointsMaterial({
            color: 0xAAAAAA,
            size: 0.2,
            transparent: true,
            opacity: 0.8,
            sizeAttenuation: true
        });

        // Create particle system
        const particles = new THREE.Points(particleGeometry, particleMaterial);
        this.scene.add(particles);

        // Create animation for particles
        const startTime = this.clock.getElapsedTime();
        const duration = 1.0;

        // Store velocity for each particle
        const velocities = [];
        for (let i = 0; i < particleCount; i++) {
            // Velocity based on collision normal and randomness
            const vel = new THREE.Vector3(
                normal.x + (Math.random() - 0.5) * 2,
                normal.y + Math.random() * 2, // Bias upward
                normal.z + (Math.random() - 0.5) * 2
            );
            vel.normalize().multiplyScalar(2 + Math.random() * 3 * (impactForce / 100));
            velocities.push(vel);
        }

        // Animation loop for particles
        const animateParticles = () => {
            // Skip if game is stopped
            if (!this.isRunning) {
                this.scene.remove(particles);
                return;
            }

            const currentTime = this.clock.getElapsedTime();
            const elapsed = currentTime - startTime;

            // Update particle positions
            const positions = particleGeometry.attributes.position.array;
            const sizes = particleGeometry.attributes.size.array;

            for (let i = 0; i < particleCount; i++) {
                // Apply gravity and velocity
                const vel = velocities[i];
                vel.y -= 5 * this.clock.getDelta(); // Apply gravity

                // Update position
                positions[i * 3] += vel.x * this.clock.getDelta();
                positions[i * 3 + 1] += vel.y * this.clock.getDelta();
                positions[i * 3 + 2] += vel.z * this.clock.getDelta();

                // Fade out size
                const fadeFactor = 1 - (elapsed / duration);
                sizes[i] *= fadeFactor;
            }

            // Update geometry
            particleGeometry.attributes.position.needsUpdate = true;
            particleGeometry.attributes.size.needsUpdate = true;

            // Update opacity
            particleMaterial.opacity = 1 - (elapsed / duration);

            // Remove particles when animation is complete
            if (elapsed >= duration) {
                this.scene.remove(particles);
                particleGeometry.dispose();
                particleMaterial.dispose();
            } else {
                requestAnimationFrame(animateParticles);
            }
        };

        // Start animation
        animateParticles();
    }

    // Apply camera shake effect
    applyCameraShake(intensity, duration) {
        // Initialize camera targets if not already defined
        if (!this.currentCameraTarget) {
            this.currentCameraTarget = new THREE.Vector3(0, 0, 0);
            if (this.camera) {
                this.camera.getWorldDirection(this.currentCameraTarget);
                this.currentCameraTarget.add(this.camera.position);
            }
        }
        
        if (!this.currentCameraPosition) {
            this.currentCameraPosition = new THREE.Vector3();
            if (this.camera) {
                this.currentCameraPosition.copy(this.camera.position);
            }
        }
        
        // Store original camera target and position for restoration
        const originalTarget = this.currentCameraTarget.clone();
        const originalPosition = this.currentCameraPosition.clone();

        // Track shake start time
        const startTime = this.clock.getElapsedTime();

        // Shake animation function
        const shakeCamera = () => {
            // Get elapsed time
            const currentTime = this.clock.getElapsedTime();
            const elapsed = currentTime - startTime;

            // Skip if shake duration has elapsed
            if (elapsed >= duration) {
                // End shake by setting target shake amount to zero
                this.cameraShakeAmount = 0;
                return;
            }

            // Calculate decreasing intensity
            const currentIntensity = intensity * (1 - elapsed / duration);

            // Apply random offset to camera position and target
            const randomOffsetX = (Math.random() - 0.5) * currentIntensity;
            const randomOffsetY = (Math.random() - 0.5) * currentIntensity;
            const randomOffsetZ = (Math.random() - 0.5) * currentIntensity;

            // Apply to camera target and position
            this.camera.position.x += randomOffsetX;
            this.camera.position.y += randomOffsetY;
            this.camera.position.z += randomOffsetZ;

            // Queue next shake
            requestAnimationFrame(shakeCamera);
        };

        // Start shake animation
        shakeCamera();
    }

    // Add a new method to add a title to the minimap
    addMinimapTitle(container) {
        const titleElement = document.createElement('div');

        titleElement.style.position = 'absolute';
        titleElement.style.top = '5px';
        titleElement.style.left = '10px';
        titleElement.style.color = 'white';
        titleElement.style.fontSize = '12px';
        titleElement.style.fontFamily = 'Arial, sans-serif';
        titleElement.style.fontWeight = 'bold';
        titleElement.style.userSelect = 'none';
        container.appendChild(titleElement);
    }


    showCameraModeIndicator(mode = 'Follow') {
        // Use UIManager to show camera mode indicator
        if (this.uiManager) {
            this.uiManager.showCameraModeIndicator(mode);
        }
    }

    configureUISettings() {
        if (!this.uiManager || !this.cameraManager) return;
        const controllerTuning = this.inputController?.getControllerTuning?.() || {
            deadzone: 0.1,
            sensitivity: 1,
            vibrationIntensity: 0.75
        };
        this.uiManager.setSettingsHandlers({
            onTogglePerformance: (enabled) => this.applyPerformanceMode(enabled),
            onToggleNightMode: (enabled) => this.applyNightMode(enabled),
            onCameraDistance: (distance) => {
                this.cameraManager.cameraDistance = distance;
                this.showCameraModeIndicator(`Distance ${Math.round(distance)}`);
            },
            onCameraFov: (fov) => {
                this.camera.fov = fov;
                this.camera.updateProjectionMatrix();
                this.showCameraModeIndicator(`FOV ${Math.round(fov)}`);
            },
            onControllerDeadzone: (value) => this.inputController?.setControllerDeadzone?.(value),
            onControllerSensitivity: (value) => this.inputController?.setSteeringSensitivity?.(value),
            onVibrationIntensity: (value) => this.inputController?.setVibrationIntensity?.(value),
            onToggleFpsOverlay: (enabled) => { this.performanceOverlayEnabled = enabled; },
            onAccessibilityPreset: (preset) => this.setAccessibilityPreset(preset),
            performanceEnabled: this.performanceMode,
            nightModeEnabled: this.nightModeEnabled,
            cameraDistance: this.cameraManager.cameraDistance,
            cameraFov: this.camera.fov,
            controllerDeadzone: controllerTuning.deadzone,
            controllerSensitivity: controllerTuning.sensitivity,
            vibrationIntensity: controllerTuning.vibrationIntensity,
            fpsOverlayEnabled: this.performanceOverlayEnabled,
            accessibilityPreset: this.accessibilityPreset
        });
    }

    applyPerformanceMode(enabled) {
        this.performanceMode = enabled;
        const targetPixelRatio = enabled ? 1 : Math.min(window.devicePixelRatio || 1, 2);
        this.renderer.setPixelRatio(targetPixelRatio);
        this.renderer.shadowMap.enabled = !enabled;

        if (this.postProcessingManager) {
            this.postProcessingManager.applyQualitySettings(enabled ? 'low' : 'high');
        }
        if (this.meteorManager) {
            this.meteorManager.qualityLevel = enabled ? 0 : 2;
        }
        this.showNotificationMessage(
            enabled ? 'Performance mode enabled' : 'Performance mode disabled',
            enabled ? 'rgba(0,120,80,0.85)' : 'rgba(50,80,180,0.85)'
        );
    }

    applyNightMode(enabled, notify = true) {
        this.nightModeEnabled = !!enabled;

        if (this.scene) {
            const bg = enabled ? 0x050b18 : ENVIRONMENT.BACKGROUND_COLOR;
            const fog = enabled ? 0x091426 : ENVIRONMENT.FOG_COLOR;
            this.scene.background = new THREE.Color(bg);
            if (this.scene.fog) {
                this.scene.fog.color.setHex(fog);
            } else {
                this.scene.fog = new THREE.FogExp2(fog, ENVIRONMENT.FOG_DENSITY);
            }
        }

        if (this.sunLight) {
            this.sunLight.intensity = enabled ? 0.65 : (this.baseSunIntensity ?? 4.0);
            this.sunLight.color.setHex(enabled ? 0x9eb8ff : 0xffffff);
        }
        if (this.ambientLight) {
            this.ambientLight.intensity = enabled ? 0.22 : (this.baseAmbientIntensity ?? 0.5);
            this.ambientLight.color.setHex(enabled ? 0x7f9bde : 0xe0e0ff);
        }

        if (this.skybox?.material?.uniforms) {
            const u = this.skybox.material.uniforms;
            if (u.topColor) u.topColor.value.setHex(enabled ? 0x020611 : 0x1A2F7A);
            if (u.middleColor) u.middleColor.value.setHex(enabled ? 0x0b1a34 : 0x7FBAEA);
            if (u.bottomColor) u.bottomColor.value.setHex(enabled ? 0x11203d : 0xF0EFE0);
            if (u.exponent) u.exponent.value = enabled ? 0.55 : 0.4;
        }

        if (Array.isArray(this.clouds) && this.clouds.length > 0) {
            const cloudOpacity = enabled ? 0.28 : 0.7;
            this.clouds.forEach((cloud) => {
                if (cloud?.material) {
                    cloud.material.opacity = cloudOpacity;
                    cloud.material.needsUpdate = true;
                }
            });
        }

        this.uiManager?.setNightModeState?.(this.nightModeEnabled);

        if (notify) {
            this.showNotificationMessage(
                this.nightModeEnabled ? 'Night mode enabled' : 'Night mode disabled',
                this.nightModeEnabled ? 'rgba(36,64,130,0.86)' : 'rgba(70,120,185,0.82)'
            );
        }
    }

    setAccessibilityPreset(preset = 'default') {
        this.accessibilityPreset = preset;
        this.minimapManager?.setAccessibilityPreset?.(preset);
        this.meteorManager?.setAccessibilityPreset?.(preset);
        this.uiManager?.applyAccessibilityPreset?.(preset);
    }


    setupEventListeners() {
        // Add window resize handler
        window.addEventListener('resize', this.onWindowResize.bind(this));

        // fuel_updated listener is registered in prepare()/start() — not here

        // Add keyboard event listener for game controls
        window.addEventListener('keydown', this.handleKeyDown.bind(this));

        // Add double-click handler for resetting camera view
        this.renderer.domElement.addEventListener('dblclick', () => {
            if (this.cameraManager) {
                this.cameraManager.resetCameraView();
            }
        });
        
        // Listen for meteor event trigger from WelcomeScreen
        window.addEventListener('trigger_meteor_event', () => {
            this.triggerMeteorEvent();
        });
    }
    
    // Trigger a meteor event
    triggerMeteorEvent() {
        console.log("Triggering meteor event");
        
        if (this.meteorManager) {
            const triggered = this.meteorManager.triggerMeteorEvent();
            if (triggered) {
                this.showNotificationMessage("METEOR SHOWER INCOMING!", "rgba(255, 50, 50, 0.8)");
            }
            return triggered;
        }
        return false;
    }

    showControlsInfo() {
        if (this.uiManager) {
            this.uiManager.showControlsInfo();
        }
    }

    handleKeyDown(e) {
        // Toggle debug mode with F2
        if (e.key === 'F2') {
            this.debugManager.toggle();
        }

        // Toggle quick settings panel
        if (e.key === 'F4') {
            const visible = this.uiManager?.toggleSettingsPanel();
            this.showNotificationMessage(
                visible ? 'Quick settings shown (F4)' : 'Quick settings hidden (F4)',
                'rgba(45, 90, 170, 0.85)'
            );
        }

        if (e.key === 'F6') {
            const visible = this.uiManager?.togglePerformanceOverlay();
            this.performanceOverlayEnabled = !!visible;
            this.showNotificationMessage(
                visible ? 'FPS overlay enabled (F6)' : 'FPS overlay hidden (F6)',
                'rgba(20, 100, 150, 0.82)'
            );
        }

        if (e.key === 'n' || e.key === 'N') {
            this.applyNightMode(!this.nightModeEnabled);
        }
        
        // Toggle collision visualization with F3
        if (e.key === 'F3') {
            this.debugManager.toggleCollisionVisualization(!this.debugMode);
        }
        
        // Toggle camera mode with C
        if (e.key === 'c' || e.key === 'C') {
            this.cameraManager?.toggleCameraMode();
        }
        
        // Toggle headlights with L
        if (e.key === 'l' || e.key === 'L') {
            if (this.bus) {
                this.bus.toggleHeadlights();
            }
        }

        if (e.key === 'v' || e.key === 'V') {
            if (this.cameraManager) {
                const mode = this.cameraManager.toggleCameraMode();
                this.showCameraModeIndicator(mode);
            }
        }
        
        // Horn when H key is pressed
        if (e.key === 'h' || e.key === 'H') {
            if (this.audioManager) {
                this.audioManager.playHorn();
            }
        }

        // Toggle performance mode
        if (e.key === 'p' || e.key === 'P') {
            this.applyPerformanceMode(!this.performanceMode);
        }
        
        // Show controls when Tab key is pressed
        if (e.key === 'Tab') {
            if (this.uiManager) {
                this.uiManager.toggleControlsInfo();
                e.preventDefault(); // Prevent default tab behavior
            }
        }
        
        /** Manual meteor event trigger with K key
        if (e.key === 'k' || e.key === 'K') {
            this.triggerMeteorEvent();
        }

        */

        /**
        // Refill fuel when G key is pressed
        if (e.key === 'g' || e.key === 'G') {
            this.refillFuel();
        }
        */

        // Camera adjustments (only in follow mode)
        if (this.cameraManager && this.cameraManager.followCameraEnabled) {
            // Adjust camera height with keys E (up) and Q (down)
            if (e.key === 'e' || e.key === 'E') {
                this.cameraManager.cameraHeight += 1;
                this.showCameraModeIndicator('Follow (Height +)');
            }

            if (e.key === 'q' || e.key === 'Q') {
                this.cameraManager.cameraHeight = Math.max(2, this.cameraManager.cameraHeight - 1);
                this.showCameraModeIndicator('Follow (Height -)');
            }

            // Adjust camera distance with keys R (increase) and F (decrease)
            if (e.key === 'r' || e.key === 'R') {
                this.cameraManager.cameraDistance += 2;
                this.showCameraModeIndicator('Follow (Distance +)');
            }

            if (e.key === 'f' || e.key === 'F') {
                this.cameraManager.cameraDistance = Math.max(5, this.cameraManager.cameraDistance - 2);
                this.showCameraModeIndicator('Follow (Distance -)');
            }
        }
    }

    // Prepare game objects without starting game loop or active systems
    prepare() {
        console.log("[Game] Preparing game objects in paused state...");
        
        // Initialize UI elements
        this.#initializeUI();
        
        // Initialize audio system without playing sounds
        this.#initializeAudioSystem();
        
        // Setup fuel update listener (mark added so start() won't duplicate it)
        if (!this.fuelListenerAdded) {
            this._boundUpdateFuelDisplay = this.updateFuelDisplay.bind(this);
            window.addEventListener('fuel_updated', this._boundUpdateFuelDisplay);
            this.fuelListenerAdded = true;
        }

        // Add coins after a short delay to ensure physics is fully ready
        if (!this.coinsAdded && this.collectibleManager) {
            setTimeout(() => {
                if (this.physics.isInitialized) {
                    this.collectibleManager.initializeCoins();
                } else {
                    console.error("Physics not initialized after delay");
                }
            }, 500);
            this.coinsAdded = true;
        }

        // Explicitly disable meteor system
        if (this.meteorManager) {
            console.log("[Game] Ensuring meteor manager is disabled during preparation");
            this.meteorManager.setEnabled(false);
            if (this.meteorManager.config) {
                this.meteorManager.config.eventInProgress = false;
                this.meteorManager.config.meteorsSpawned = false;
                this.meteorManager.config.nextEventTime = Number.MAX_SAFE_INTEGER;
            }
        }
        
        // Hide loading screen
        this.#hideLoadingScreen();
        
        // Start a minimal animation loop for rendering the scene without game logic
        this.prepareRenderLoop();
        
        console.log("[Game] Game prepared with visible scene. Waiting for start signal.");
    }
    
    // Start a minimal render loop to show the scene without game logic
    prepareRenderLoop() {
        // Make sure we're not double-starting loops
        if (this.preparationLoopActive) return;
        
        this.preparationLoopActive = true;
        
        // Minimal animation function that only handles rendering
        const renderLoop = () => {
            // If full game is running, stop this minimal loop
            if (this.isRunning) {
                this.preparationLoopActive = false;
                return;
            }
            
            // Update camera for a good view
            if (this.cameraManager) {
                this.cameraManager.updateCamera(0.016); // Approximate delta time (60fps)
            }
            
            // Render the scene
            if (this.renderer && this.scene && this.camera) {
                this.renderer.render(this.scene, this.camera);
            }
            
            // Render minimap if available
            if (this.minimapManager && this.minimapRenderer && this.minimapCamera) {
                this.minimapRenderer.render(this.scene, this.minimapCamera);
            }
            
            // Continue loop if game hasn't started yet
            if (!this.isRunning) {
                requestAnimationFrame(renderLoop);
            }
        };
        
        // Start the loop
        requestAnimationFrame(renderLoop);
    }
}
